import { useCallback, useEffect, useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'
import { fetchWikiMedia, uploadWikiMedia } from '../api/client'
import type { WikiMedia } from '../types/wiki'

interface MediaPickerProps {
  open: boolean
  onClose: () => void
  onSelect: (corpusPath: string) => void
  canUpload: boolean
}

export default function MediaPicker({ open, onClose, onSelect, canUpload }: MediaPickerProps) {
  const [media, setMedia] = useState<WikiMedia[]>([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const { media: items } = await fetchWikiMedia()
      setMedia(items)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to load media'
          : 'Failed to load media'
      setErr(msg)
      setMedia([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    void load()
  }, [open, load])

  const handleUpload = async (file: File) => {
    setUploading(true)
    setErr(null)
    try {
      const item = await uploadWikiMedia(file)
      setMedia((prev) => [item, ...prev])
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Upload failed'
          : 'Upload failed'
      setErr(msg)
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[80vh] flex flex-col rounded-xl border border-gray-700 bg-gray-950 shadow-xl"
        role="dialog"
        aria-label="Media library"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-gray-100">Media library</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-800 hover:text-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-800 flex gap-2">
          {canUpload && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void handleUpload(f)
                  e.target.value = ''
                }}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 min-h-[44px] px-3 rounded-lg text-sm font-medium bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50"
              >
                <ImagePlus className="w-4 h-4" />
                {uploading ? 'Uploading…' : 'Upload image'}
              </button>
            </>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {err && <p className="text-error-400 text-sm mb-3">{err}</p>}
          {loading && <p className="text-gray-400 text-sm">Loading media…</p>}
          {!loading && media.length === 0 && (
            <p className="text-gray-500 text-sm">No media uploaded yet.</p>
          )}
          <ul className="grid grid-cols-2 gap-3">
            {media.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(item.corpus_path)
                    onClose()
                  }}
                  className="w-full text-left rounded-lg border border-gray-700 bg-gray-900/60 hover:border-primary-700/50 hover:bg-gray-900 p-2 min-h-[44px]"
                >
                  <img
                    src={`/api/doc/${encodeURIComponent(item.corpus_path)}`}
                    alt={item.filename}
                    className="w-full h-24 object-cover rounded mb-2 bg-gray-800"
                    loading="lazy"
                  />
                  <span className="text-xs text-gray-400 truncate block">{item.filename}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
